/**
 * Elements Lock Service – advisory locking for collaborative editing.
 *
 * API endpoints:
 * - POST /checkpoints/{spreadId}/layers/{layerName}/elements/lock
 * - POST /checkpoints/{spreadId}/layers/{layerName}/elements/unlock
 *
 * Body is a plain array of objectIds: [objectId1, objectId2, ...]
 *
 * Locks are advisory (fire-and-forget). The frontend should treat lock
 * notifications as binding by showing visual indicators and preventing
 * interaction with locked elements.
 */

import { httpClient } from '@/api/http/client'
import type { ElementLayer } from '@/api/types/stowed-element'

export type LockElementsResponse = {
  owned?: boolean
  locked?: boolean
  expiration?: string
}

export type UnlockElementsResponse = {
  message?: string
}

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function normalizeIds(values: string[]): string[] {
  const normalized = values
    .map((v) => {
      const trimmed = v.trim()

      if (!trimmed) return ''
      const last = trimmed.split('/').filter(Boolean).pop()

      return last ?? trimmed
    })
    .filter((v) => v.length > 0)

  return Array.from(new Set(normalized))
}

/**
 * Lock elements before editing (fire-and-forget advisory lock).
 * POST /checkpoints/{spreadId}/layers/{layerName}/elements/lock
 * Body: [objectId1, objectId2, ...]
 */
async function lockElements(
  spreadId: string,
  layer: ElementLayer,
  elementObjectIds: string[]
): Promise<LockElementsResponse> {
  const ids = normalizeIds(elementObjectIds)

  return await httpClient.post<string[], LockElementsResponse>(
    `/checkpoints/${enc(spreadId)}/layers/${enc(layer)}/elements/lock`,
    ids
  )
}

/**
 * Unlock elements after editing is complete.
 * POST /checkpoints/{spreadId}/layers/{layerName}/elements/unlock
 * Body: [objectId1, objectId2, ...]
 */
async function unlockElements(
  spreadId: string,
  layer: ElementLayer,
  elementObjectIds: string[]
): Promise<UnlockElementsResponse> {
  const ids = normalizeIds(elementObjectIds)

  return await httpClient.post<string[], UnlockElementsResponse>(
    `/checkpoints/${enc(spreadId)}/layers/${enc(layer)}/elements/unlock`,
    ids
  )
}

export const elementsLockService = {
  lockElements,
  unlockElements,
}
